import {
  isProcurementLockedForEdits,
  ProcurementState,
} from "@boark/shared/types/procurement";
import { ProcurementWithCreatedByAndOrganizations } from "@boark/shared/types/procurement-with-created-by-and-organizations";
import { useMemo } from "react";
import { Outlet, Route, Routes, useSearchParams } from "react-router-dom";

import { Button } from "../../components/button";
import { H1 } from "../../components/h1";
import { Column, Filter, Table } from "../../components/table";
import { useGetProcurements } from "../../queries/procurement";
import {
  formatProcurementOrganizationLabel,
  uniqueSortedTagSuggestions,
} from "../../services/procurement";
import { useLocale } from "../../stores/locale";
import { formatDate } from "../../utils/date";
import { recordIdToId } from "../../utils/record-id-to-id";
import { ProcurementCreate } from "./procurement-create";
import { ProcurementDelete } from "./procurement-delete";
import { ProcurementEdit } from "./procurement-edit";

export function ProcurementList() {
  const { locale: t, currentLanguage } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: procurements, isLoading: isProcurementsLoading } =
    useGetProcurements();

  const tagFilterOptions = useMemo(
    () =>
      uniqueSortedTagSuggestions(procurements).map((tag) => ({
        label: tag,
        value: tag,
      })),
    [procurements],
  );

  function updateSearchParams(params: Record<string, string>) {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      ...params,
    });
  }

  function stateLabel(state: ProcurementState) {
    switch (state) {
      case ProcurementState.OPEN:
        return t.procurement.stateOpen;
      case ProcurementState.IN_REVIEW:
        return t.procurement.stateInReview;
      case ProcurementState.CLOSED:
        return t.procurement.stateClosed;
      case ProcurementState.CANCELED:
        return t.procurement.stateCanceled;
    }
  }

  return (
    <>
      <Routes>
        <Route element={<ProcurementCreate />} path="/create" />
        <Route element={<ProcurementEdit />} path="/:id/edit" />
        <Route element={<ProcurementDelete />} path="/:id/delete" />
      </Routes>
      <Outlet />
      <div className="w-full min-h-full overflow-x-auto">
        <div className="flex justify-between items-center">
          <H1>{t.procurement.procurements}</H1>
          <Button route="/admin/procurement/list/create">
            {t.procurement.create}
          </Button>
        </div>
        <Table
          data={procurements || []}
          loading={isProcurementsLoading}
          editRouteButton={(row) =>
            isProcurementLockedForEdits(row.state)
              ? ""
              : `/admin/procurement/list/${recordIdToId(row.id)}/edit`
          }
          filterBy={
            JSON.parse(searchParams.get("filterBy") || "[]") as Filter[]
          }
          locale={{ yes: t.yes, no: t.no, actions: t.action.actions, empty: t.empty }}
          removeRouteButton={(row) =>
            `/admin/procurement/list/${recordIdToId(row.id)}/delete`
          }
          sortBy={searchParams.get("sortBy") || "title"}
          sortByDirection={
            searchParams.get("sortByDirection") as "asc" | "desc"
          }
          viewRouteButton={(row) =>
            `/admin/procurement/${recordIdToId(row.id)}`
          }
          onFilter={(value: Filter[]) => {
            updateSearchParams({
              filterBy: JSON.stringify(value),
            });
          }}
          onSort={(field, direction) => {
            updateSearchParams({
              sortBy: field,
              sortByDirection: direction,
            });
          }}
        >
          <Column field="title">{t.procurement.title}</Column>
          <Column
            body={(row: ProcurementWithCreatedByAndOrganizations) =>
              row.organizations.length > 0
                ? row.organizations
                    .map((organization) =>
                      formatProcurementOrganizationLabel(
                        organization,
                        currentLanguage,
                      ),
                    )
                    .join(", ")
                : "-"
            }
            field="organizations"
            filterableBy="text"
          >
            {t.organization.organizations}
          </Column>
          <Column
            body={(row: ProcurementWithCreatedByAndOrganizations) =>
              stateLabel(row.state)
            }
            field="state"
            filterableBy="select"
            filterOptions={() => [
              {
                label: t.procurement.stateOpen,
                value: ProcurementState.OPEN,
              },
              {
                label: t.procurement.stateInReview,
                value: ProcurementState.IN_REVIEW,
              },
              {
                label: t.procurement.stateClosed,
                value: ProcurementState.CLOSED,
              },
              {
                label: t.procurement.stateCanceled,
                value: ProcurementState.CANCELED,
              },
            ]}
          >
            {t.procurement.state}
          </Column>
          <Column
            body={(row: ProcurementWithCreatedByAndOrganizations) =>
              row.tags.length > 0 ? row.tags.join(", ") : "-"
            }
            field="tags"
            filterableBy="select"
            filterOptions={() => tagFilterOptions}
          >
            {t.procurement.tags}
          </Column>
          <Column
            body={(row: ProcurementWithCreatedByAndOrganizations) =>
              row.createdBy.email
            }
            field="createdBy.email"
            filterableBy="text"
          >
            {t.procurement.createdBy}
          </Column>
          <Column
            body={(row: ProcurementWithCreatedByAndOrganizations) =>
              formatDate(row.createdAt)
            }
            field="createdAt"
            filterableBy="date"
          >
            {t.procurement.createdAt}
          </Column>
          <Column
            body={(row: ProcurementWithCreatedByAndOrganizations) =>
              formatDate(row.updatedAt)
            }
            field="updatedAt"
            filterableBy="date"
          >
            {t.procurement.updatedAt}
          </Column>
        </Table>
      </div>
    </>
  );
}
